import { dreamityPlannerRepository } from '@/services/dreamityPlannerRepository'
import type { Dream } from '@/store/types/dream.types'
import type {
  DashboardActionItem,
  DashboardOverallStats,
  DashboardProgressItem,
  ProgressStatus,
} from '@/store/types/dashboard.types'

function dreamCompletionPercent(dream: Dream): number {
  const r = dream.completionRate
  if (r == null || Number.isNaN(Number(r))) return 0
  const n = Number(r)
  return n > 1 ? Math.min(100, n) : Math.min(100, n * 100)
}

function deriveProgressStatus(
  completionPercent: number,
  planCount: number,
  completedPlanCount: number
): ProgressStatus {
  if (
    completionPercent >= 99.5 ||
    (planCount > 0 && completedPlanCount === planCount)
  ) {
    return '완료'
  }
  if (completionPercent > 0 || completedPlanCount > 0) {
    return '진행중'
  }
  return '준비'
}

/**
 * 로컬 스냅샷(dreamityPlannerRepository)만으로 대시보드용 집계 생성.
 */
export function buildLocalDashboardData(
  startYmd: string,
  endYmd: string
): {
  overall: DashboardOverallStats
  progress: DashboardProgressItem[]
  actions: DashboardActionItem[]
} {
  const bundle = dreamityPlannerRepository.readDomainsForDashboard()
  const emptyOverall: DashboardOverallStats = {
    totalDreamCount: 0,
    totalPlanCount: 0,
    totalItemCount: 0,
    totalBudget: 0,
  }
  if (!bundle) {
    return { overall: emptyOverall, progress: [], actions: [] }
  }

  const { dreams, plans, items } = bundle

  const totalBudget = dreams.reduce(
    (s, d) => s + (typeof d.budget === 'number' ? d.budget : 0),
    0
  )

  const overall: DashboardOverallStats = {
    totalDreamCount: dreams.length,
    totalPlanCount: plans.length,
    totalItemCount: items.length,
    totalBudget,
  }

  const progress: DashboardProgressItem[] = dreams.map((dream) => {
    const plansForDream = plans.filter((p) => p.dreamId === dream.dreamId)
    const planCount = plansForDream.length
    const completedPlanCount = plansForDream.filter((p) => p.isCompleted).length
    const itemsForDream = items.filter((i) => i.dreamId === dream.dreamId)
    const itemAmount = itemsForDream.reduce(
      (s, i) => s + (typeof i.itemPrice === 'number' ? i.itemPrice : 0),
      0
    )

    const completionPercent =
      dream.completionRate != null
        ? dreamCompletionPercent(dream)
        : planCount > 0
          ? (completedPlanCount / planCount) * 100
          : 0

    const progressStatus = deriveProgressStatus(
      completionPercent,
      planCount,
      completedPlanCount
    )

    return {
      dreamId: dream.dreamId,
      dreamName: dream.dreamName,
      dreamDesc: dream.dreamDesc || null,
      startDate: dream.startDate ?? null,
      endDate: dream.endDate ?? null,
      planCount,
      completedPlanCount,
      completionPercent,
      targetAmount: typeof dream.budget === 'number' ? dream.budget : 0,
      itemAmount,
      progressStatus,
    }
  })

  const startCompact = startYmd.replace(/-/g, '')
  const endCompact = endYmd.replace(/-/g, '')

  const inRange = dreamityPlannerRepository.listActionsInRange(startCompact, endCompact)

  const mapped: DashboardActionItem[] = inRange.map((a) => ({
    dreamId: a.dreamId,
    goalId: a.goalId,
    planId: a.planId,
    actionId: a.actionId ?? '',
    actionDate: a.actionDate,
    actionTime: a.actionTime ?? null,
    actionName: a.actionName,
    actionDesc: a.actionDesc || null,
    isCompleted: a.isCompleted,
  }))

  return { overall, progress, actions: mapped }
}
